import { MatDialog, MatDialogConfig } from '@angular/material/dialog';

import { IContextMenuItem } from '../../interfaces/icontext-menu-item.interface';
import { ContextMenuItemBase } from '../menu/context-menu-item-base';
import { StructureComponent } from '../../../views/site/structure/structure.component';
import { Folder } from '../../models/site-content/folder.model';
import { IListItem } from '../../interfaces/ilist-item.interface';
import { FormInstance } from '../../models/site-content/form-instance.model';
import { CopyFormInstanceDialog } from '../../dialogs/copy-form-instance/copy-form-instance.dialog';
import { ClipboardService } from '../../services/clipboard.service';
import { ItemTypeEnum } from '../../enums/item-type.enum';
import { FolderService } from '../../services/folder.service';
import { CurrentSiteService } from '../../services/current-site.service';
import { MagicStructureSetupDialog } from '../../dialogs/magic-structure-setup/magic-structure-setup.dialog';
import { ExportStructureInstancesDialog } from '../../dialogs/export-structure/export-structure.dialog';
import { IHasIdAndName } from '../../interfaces/has-id-and-name.interface';
import { FormInstanceService } from '../../services/form-instance.service';
import { TrashDeleteEvent } from '../../components/list-view/list-view-trash-config.model';
import { StructureItemContextMenuItemBase } from './structure-context-menu-base';
import { FormsExportMenuDataBase } from '../forms/item-menu-classes';
import { XmlFileFormatEnum } from '../../enums/xml-file-format.enum';

// Define a class that implements IContextMewnuData.
/*
abstract class StructureItemContextMenuItemBase extends ContextMenuItemBase implements IContextMenuItem {
    // Private properties.
    protected readonly structureComponent: StructureComponent;

    // Implement IContextMenuItem.
    public abstract menuItemSelected(contextItem: any, eventData: any): void;

    // Constructor.
    public constructor(itemTitle: string, structureComponent: StructureComponent) {
        super(itemTitle);
        
        this.structureComponent = structureComponent;
    }
}
*/

// Define menu classes.
export class OpenItemContextMenuData extends StructureItemContextMenuItemBase {
    public constructor(itemTitle: string, structureComponent: StructureComponent) {
        super(itemTitle, structureComponent);
    }

    public get MenuItemDisabled(): boolean {
        return false; // Note:  this menu item is never disabled.
    }

    public menuItemSelected(contextItem: any, eventData: any): void {
        let displayEntityParam = {
            'originalEvent': eventData,
            'item': this.contextItem
        };
        this.structureComponent.displayEntity(displayEntityParam);
    }
}

export class CopyItemContextMenuData extends StructureItemContextMenuItemBase {
    // Constructor.
    public constructor(itemTitle: string, structureComponent: StructureComponent, private dialog: MatDialog) {
        super(itemTitle, structureComponent);
    }

    public get MenuItemDisabled(): boolean {
        let bUserCanExport: boolean = false;

        if (this.contextItem != null) {
            let listItem: IListItem = this.contextItem;

            bUserCanExport = (listItem.getType() == FormInstance.TypeName);
        }

        return (!bUserCanExport);
    }

    public menuItemSelected(contextItem: any, eventData: any): void {
        const dialogRef = this.dialog.open(CopyFormInstanceDialog, { data: this.contextItem });

        dialogRef.afterClosed().subscribe(items => {
            if (items) {
                let list = Object.assign([], this.structureComponent.ListItems);

                for (let item of items) {
                    list.push(item as FormInstance);
                }
                this.structureComponent.ListItems = list; // this is needed because the GridView requires listItems to be replaced not just modified in place
                this.structureComponent.forceTreeRedraw();
            }
        });
    }
}

export class PasteItemContextMenuData extends StructureItemContextMenuItemBase {
    private static readonly PASTE_OPTION: string = 'Paste ...';
    private static readonly PASTE_REF_OPTION: string = 'Paste Reference ...';
    public constructor(itemTitle: string, structureComponent: StructureComponent, private clipboardService: ClipboardService, private folderService: FolderService) {
        super(itemTitle, structureComponent);
    }

    // override
    public get menuItemTitle(): string {
        let copiedIsRef = this.clipboardService.AllCopiedAreReferences;
        if (copiedIsRef && this.itemTitle == PasteItemContextMenuData.PASTE_OPTION) {
            return 'Paste Copy of Original';
        } else if (copiedIsRef && this.itemTitle == PasteItemContextMenuData.PASTE_REF_OPTION) {
            return 'Paste Another Reference';
        } else {
            return this.itemTitle;
        }
    }

    public get MenuItemDisabled(): boolean {
        return (!this.clipboardService.hasCopiedItemsOfType([ItemTypeEnum.FOLDER, ItemTypeEnum.FORM_INSTANCE]));
    }

    public menuItemSelected(contextItem: any, eventData: any): void {
        let pasteRef = this.itemTitle == "Paste Reference ...";
        this.doPasteFormInstance(pasteRef);
    }

    // Helper methods.
    protected doPasteFormInstance(asReference: boolean = false) {
        let pasteTo = this.contextItem;
        let folderId = pasteTo.id;
        if (pasteTo instanceof FormInstance) {
            folderId = pasteTo.folderId;
        }
        let copiedFormInstances = this.clipboardService.CopiedItems as FormInstance[];
        this.folderService.copyFormInstances(folderId, copiedFormInstances, asReference).then(newReferences => {
            this.structureComponent.reloadStructure(); //update icon counters
            this.structureComponent.forceTreeRedraw();
        });
    }
}
export class PasteItemReferenceMenuData extends PasteItemContextMenuData {
    public constructor(itemTitle: string, structureComponent: StructureComponent, clipboardService: ClipboardService, folderService: FolderService) {
        super(itemTitle, structureComponent, clipboardService, folderService)
    }

    public menuItemSelected(contextItem: any, eventData: any): void {
        this.doPasteFormInstance(true);
    }
}

export class MagicStructureItemContextMenuData extends StructureItemContextMenuItemBase {
    public constructor(structureComponent: StructureComponent, private currentSiteService: CurrentSiteService, private dialog: MatDialog) {
        super(null, structureComponent);
    }

    public get menuItemTitle(): string {
        let menuItemText: string = 'Create Magic Folder ...';

        if (this.contextItem != null) {
            menuItemText = (this.contextItem.getType() == Folder.TypeName ?
                'Create Magic Folder ...' : 'Create Magic Form ...');
        }

        return (menuItemText);
    }

    public get MenuItemDisabled(): boolean {
        let bUserCanMakeTemplate: boolean = false;

        if (this.contextItem != null)  {
            let contextItem: IListItem = this.contextItem;

            bUserCanMakeTemplate = (this.currentSiteService.Site.siteIsAdministerable && (!contextItem.isMagicStructure()));
        }

        return (!bUserCanMakeTemplate);
    }

    public menuItemSelected(contextItem: any, eventData: any): void {
        // Launch a create magic structure dialogue.
        let itemType: string = this.contextItem.getType();

        let dialogConfig: MatDialogConfig = new MatDialogConfig();
        dialogConfig.hasBackdrop = true;
        dialogConfig.width = '850px';
        dialogConfig.height = '750px';
        dialogConfig.data = this.contextItem;

        const dialogRef = this.dialog.open(MagicStructureSetupDialog, dialogConfig);

        dialogRef.afterClosed().subscribe(magicStructureDef => {
            if (magicStructureDef != null) {
                this.structureComponent.reloadCurrentFolder();
            }
        });
    }
}

export class SendNotificationItemContextMenuData extends StructureItemContextMenuItemBase {
    public constructor(itemTitle: string, structureComponent: StructureComponent, private currentSiteService: CurrentSiteService) {
        super(itemTitle, structureComponent);
    }

    public get MenuItemDisabled(): boolean {
        let userCanSend: boolean = false;

        if (this.contextItem != null) {
            let contextItem: IListItem = this.contextItem;
            userCanSend = (this.currentSiteService.Site.siteIsAdministerable && contextItem.isMagicStructure());
        }

        return !userCanSend;
    }

    public menuItemSelected(contextItem: any, eventData: any): void {
        this.structureComponent.sendNotification(eventData)
    }
}

export class ExportAllCopiesItemContextMenuData extends StructureItemContextMenuItemBase {
    public constructor(itemTitle: string, structureComponent: StructureComponent, private dialog: MatDialog, private formInstanceService: FormInstanceService) {
        super(itemTitle, structureComponent);
    }

    public get MenuItemDisabled(): boolean {
        let bUserCanExport: boolean = false;

        if (this.contextItem != null) {
            let contextItem: IListItem = this.contextItem;

            bUserCanExport = (contextItem.getType() == FormInstance.TypeName);
        }

        return (!bUserCanExport);
    }

    public menuItemSelected(contextItem: any, eventData: any): void {
        // Process an export structure instances dialogue
        //
        // Note:  presently this dialogue only exports
        //        form instances.
        let contextMenuItem: IHasIdAndName = this.contextItem;
        let formInstance: FormInstance = <FormInstance>contextMenuItem;

        this.formInstanceService.get(formInstance.id).then(fullFormInstance => {
            /*
            let dialogConfig: MatDialogConfig = new MatDialogConfig();
            dialogConfig.hasBackdrop = true;
            dialogConfig.width = '500px';
            dialogConfig.data = fullFormInstance.form;//contextMenuItem; //this.contextMenu.menuData.item;

            const dialogRef = this.dialog.open(ExportStructureInstancesDialog, dialogConfig);

            dialogRef.afterClosed().subscribe(exportedFormId => {
                // Note:  the export is performed in the dialog component class.
                if ((exportedFormId != null) && (exportedFormId > 0)) {
                    // Anything left to do here?  Nope!
                }
            });
            */
            const dialogRef = FormsExportMenuDataBase.createExportStructureDialogueRef(this.dialog, fullFormInstance.form);
            let dialogInstance: ExportStructureInstancesDialog = dialogRef.componentInstance;
            dialogInstance.xmlFileFormat = XmlFileFormatEnum.SIMPLIFIED_MINIMALIST_FORMAT;

            dialogRef.afterClosed().subscribe(exportedFormId => {
                // Note:  the export is performed in the dialog component class.
                if ((exportedFormId != null) && (exportedFormId > 0)) {
                    // Anything left to do here?  Nope!
                }
            });
        });
    }
}

export class DeleteItemContextMenuData extends StructureItemContextMenuItemBase {
    public constructor(itemTitle: string, structureComponent: StructureComponent) {
        super(itemTitle, structureComponent);
    }

    public get MenuItemDisabled(): boolean {
        let bUserCanDelete: boolean = false;

        if (this.contextItem != null) {
            let contextItem: IListItem = this.contextItem;

            bUserCanDelete = contextItem.userCanDelete();
        }

        return (!bUserCanDelete);
    }

    public menuItemSelected(contextItem: any, eventData: any): void {
        if (this.contextItem != null) {
            let trashEvent: TrashDeleteEvent = new TrashDeleteEvent(eventData, this.structureComponent.SelectedItems);

            this.structureComponent.doOnItemDeleted(trashEvent);
        }

        return;
    }
}

export class ScheduleEventForItemContextMenuData extends StructureItemContextMenuItemBase {
    public constructor(itemTitle: string, structureComponent: StructureComponent) {
        super(itemTitle, structureComponent);
    }

    public get MenuItemDisabled(): boolean {
        let disable: boolean = false;
        // TO DO:  CODE THIS METHOD.
        return disable;
    }

    public menuItemSelected(contextItem: any, eventData: any): void {
        // TO DO:  CODE THIS METHOD.
    }
}
